package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch
import androidx.compose.foundation.Image
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FilesScreen(
    onBack: () -> Unit,
    onNavigateToAddEdit: (FileItem?, String?) -> Unit,
    onNavigateToFolderManager: () -> Unit,
    onNavigateToImageViewer: (List<String>, Int, String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val uriHandler = LocalUriHandler.current

    var fileItems by remember { mutableStateOf<List<FileItem>>(emptyList()) }
    var folders by remember { mutableStateOf<List<FileFolder>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    var selectedFolder by remember { mutableStateOf<FileFolder?>(null) }
    var selectedSubFolder by remember { mutableStateOf<String?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var viewingItem by remember { mutableStateOf<FileItem?>(null) }
    var itemToDelete by remember { mutableStateOf<FileItem?>(null) }
    var showAddBottomSheet by remember { mutableStateOf(false) }
    var showAddFolderDialog by remember { mutableStateOf(false) }
    var newFolderName by remember { mutableStateOf("") }

    fun loadData() {
        scope.launch {
            isLoading = true
            repository.getFileFolders().onSuccess { folders = it }
            repository.getFileItems().onSuccess { fileItems = it }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) { loadData() }

    val filteredItems = fileItems.filter { item ->
        val matchesSearch = searchQuery.isBlank() ||
            item.title.contains(searchQuery, ignoreCase = true) ||
            (item.description?.contains(searchQuery, ignoreCase = true) == true)
        val matchesFolder = selectedFolder == null || item.folderId == selectedFolder?.id ||
            item.folderName.equals(selectedFolder?.folderName, ignoreCase = true)
        val matchesSub = selectedSubFolder == null || item.subFolderName.equals(selectedSubFolder, ignoreCase = true)
        matchesSearch && matchesFolder && matchesSub
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        when {
                            selectedSubFolder != null -> "Files: $selectedSubFolder"
                            selectedFolder != null -> "Files: ${selectedFolder?.folderName}"
                            else -> "Files"
                        }
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        when {
                            selectedSubFolder != null -> selectedSubFolder = null
                            selectedFolder != null -> selectedFolder = null
                            else -> onBack()
                        }
                    }) { BackArrowIcon() }
                },
                actions = {
                    ToolbarPlusButton(onClick = { showAddBottomSheet = true })
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            if (showAddBottomSheet) {
                ModalBottomSheet(onDismissRequest = { showAddBottomSheet = false }) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(24.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Text("Add New", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                        ListItem(
                            headlineContent = { Text("Folder", fontWeight = FontWeight.Bold) },
                            supportingContent = { Text(selectedFolder?.let { "Create subfolder under ${it.folderName}" } ?: "Create root folder") },
                            modifier = Modifier.clickable {
                                showAddBottomSheet = false
                                showAddFolderDialog = true
                            }
                        )
                        HorizontalDivider()
                        ListItem(
                            headlineContent = { Text("File", fontWeight = FontWeight.Bold) },
                            supportingContent = { Text("Upload a PDF, image, or other document") },
                            modifier = Modifier.clickable {
                                showAddBottomSheet = false
                                onNavigateToAddEdit(null, selectedFolder?.id)
                            }
                        )
                        HorizontalDivider()
                        ListItem(
                            headlineContent = { Text("Manage folders", fontWeight = FontWeight.Bold) },
                            supportingContent = { Text("Rename structure or delete folders") },
                            modifier = Modifier.clickable {
                                showAddBottomSheet = false
                                onNavigateToFolderManager()
                            }
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
            }

            if (showAddFolderDialog) {
                AlertDialog(
                    onDismissRequest = { showAddFolderDialog = false },
                    title = { Text(selectedFolder?.let { "Create subfolder under ${it.folderName}" } ?: "Create folder") },
                    text = {
                        OutlinedTextField(
                            value = newFolderName,
                            onValueChange = { newFolderName = it },
                            label = { Text("Folder name *") },
                            singleLine = true,
                            shape = MaterialTheme.shapes.small,
                            modifier = Modifier.fillMaxWidth()
                        )
                    },
                    confirmButton = {
                        Button(
                            onClick = {
                                if (newFolderName.isNotBlank()) {
                                    val folderName = newFolderName.trim()
                                    val parent = selectedFolder?.folderName
                                    val req = SaveFileFolderRequest(
                                        folderName = parent ?: folderName,
                                        subFolderName = if (parent != null) folderName else null
                                    )
                                    scope.launch {
                                        repository.saveFileFolder(req).onSuccess {
                                            newFolderName = ""
                                            showAddFolderDialog = false
                                            loadData()
                                            snackbarHostState.showSnackbar("Folder created")
                                        }
                                    }
                                }
                            },
                            enabled = newFolderName.isNotBlank()
                        ) { Text("Create") }
                    },
                    dismissButton = {
                        TextButton(onClick = { showAddFolderDialog = false }) { Text("Cancel") }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }

            Column(
                modifier = Modifier.fillMaxSize().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    label = { Text("Search files...") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small,
                    trailingIcon = {
                        if (searchQuery.isNotEmpty()) {
                            TextButton(onClick = { searchQuery = "" }) { Text("Clear") }
                        }
                    }
                )

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (searchQuery.isBlank() && selectedFolder == null) {
                    val distinctFolders = folders.distinctBy { it.folderName }
                    if (distinctFolders.isEmpty()) {
                        Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text("No folders yet.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                                Button(onClick = { showAddFolderDialog = true }) { Text("Create folder") }
                            }
                        }
                    } else {
                        Text("Select folder", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            items(items = distinctFolders, key = { it.id }) { folder ->
                                val count = fileItems.count {
                                    it.folderId == folder.id || it.folderName.equals(folder.folderName, ignoreCase = true)
                                }
                                FileFolderCard(
                                    name = folder.folderName,
                                    subtitle = "$count file(s)",
                                    onClick = {
                                        selectedFolder = folder
                                        selectedSubFolder = null
                                    }
                                )
                            }
                        }
                    }
                } else if (searchQuery.isBlank() && selectedFolder != null && selectedSubFolder == null) {
                    val subFolders = folders
                        .filter { it.folderName.equals(selectedFolder?.folderName, ignoreCase = true) && !it.subFolderName.isNullOrBlank() }
                        .mapNotNull { it.subFolderName }
                    if (subFolders.isNotEmpty()) {
                        Text("Subfolders in ${selectedFolder?.folderName}", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            items(items = subFolders, key = { it }) { sub ->
                                FileFolderCard(name = sub, subtitle = null, onClick = { selectedSubFolder = sub })
                            }
                        }
                    } else {
                        FileItemsList(
                            items = filteredItems,
                            onItemClick = { viewingItem = it },
                            onEdit = { onNavigateToAddEdit(it, it.folderId) },
                            onDelete = { itemToDelete = it },
                            onOpenFile = { url, title, files ->
                                openStoredFile(url, title, files, uriHandler, onNavigateToImageViewer)
                            }
                        )
                    }
                } else {
                    FileItemsList(
                        items = filteredItems,
                        onItemClick = { viewingItem = it },
                        onEdit = { onNavigateToAddEdit(it, it.folderId) },
                        onDelete = { itemToDelete = it },
                        onOpenFile = { url, title, files ->
                            openStoredFile(url, title, files, uriHandler, onNavigateToImageViewer)
                        }
                    )
                }
            }

            viewingItem?.let { item ->
                AlertDialog(
                    onDismissRequest = { viewingItem = null },
                    title = { Text(item.title) },
                    text = {
                        Column(
                            modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            if (!item.folderName.isNullOrBlank()) {
                                Text(
                                    "Folder: ${item.folderName}${item.subFolderName?.let { " -> $it" } ?: ""}",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.secondary
                                )
                            }
                            if (!item.description.isNullOrBlank()) {
                                Text(item.description, style = MaterialTheme.typography.bodyMedium)
                            }
                            item.files.orEmpty().forEach { url ->
                                TextButton(onClick = {
                                    openStoredFile(url, item.title, item.files.orEmpty(), uriHandler, onNavigateToImageViewer)
                                }) {
                                    Text(displayFileName(url), color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }
                    },
                    confirmButton = {
                        TextButton(onClick = { viewingItem = null }) { Text("Close") }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }

            itemToDelete?.let { item ->
                ConfirmDeleteDialog(
                    message = "Delete '${item.title}'?",
                    onConfirm = {
                        val id = item.id
                        itemToDelete = null
                        fileItems = fileItems.filter { it.id != id }
                        scope.launch {
                            item.files.orEmpty().forEach { url ->
                                runCatching { co.uk.sacursoftware.ssinventory.util.FirebaseStorageService().deleteOrderImage(url) }
                            }
                            repository.deleteFileItem(id)
                        }
                    },
                    onDismiss = { itemToDelete = null }
                )
            }
        }
    }
}

private fun openStoredFile(
    url: String,
    title: String,
    all: List<String>,
    uriHandler: androidx.compose.ui.platform.UriHandler,
    onNavigateToImageViewer: (List<String>, Int, String?) -> Unit
) {
    if (isImageFileUrl(url)) {
        val images = all.filter { isImageFileUrl(it) }
        val index = images.indexOf(url).coerceAtLeast(0)
        onNavigateToImageViewer(images, index, title)
    } else {
        runCatching { uriHandler.openUri(url) }
    }
}

internal fun displayFileName(url: String): String {
    val encoded = url.substringAfter("/o/").substringBefore("?")
    val path = encoded.replace("%2F", "/")
    return path.substringAfterLast('/').substringAfter('_').ifBlank { "File" }
}

internal fun isImageFileUrl(url: String): Boolean {
    val name = url.lowercase()
    return listOf(".jpg", ".jpeg", ".png", ".gif", ".webp").any { name.contains(it) }
}

@Composable
private fun FileFolderCard(name: String, subtitle: String?, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
                Image(
                    painter = painterResource(Res.drawable.folder),
                    contentDescription = "Folder",
                    modifier = Modifier.size(24.dp),
                    contentScale = ContentScale.Fit
                )
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(name, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    if (subtitle != null) {
                        Text(subtitle, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                    }
                }
            }
            Text(">", style = MaterialTheme.typography.titleLarge, color = MaterialTheme.colorScheme.outline)
        }
    }
}

@Composable
private fun FileItemsList(
    items: List<FileItem>,
    onItemClick: (FileItem) -> Unit,
    onEdit: (FileItem) -> Unit,
    onDelete: (FileItem) -> Unit,
    onOpenFile: (String, String, List<String>) -> Unit
) {
    if (items.isEmpty()) {
        Box(modifier = Modifier.fillMaxWidth().fillMaxHeight(), contentAlignment = Alignment.Center) {
            Text("No files in this folder.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
        }
    } else {
        LazyColumn(modifier = Modifier.fillMaxWidth().fillMaxHeight(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            items(items = items, key = { it.id }) { item ->
                Card(
                    modifier = Modifier.fillMaxWidth().clickable { onItemClick(item) },
                    shape = MaterialTheme.shapes.medium,
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                ) {
                    Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(item.title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                            Row {
                                TextButton(onClick = { onEdit(item) }) { Text("Edit") }
                                IconButton(onClick = { onDelete(item) }) {
                                    CloseIcon(tint = MaterialTheme.colorScheme.error)
                                }
                            }
                        }
                        if (!item.folderName.isNullOrBlank()) {
                            Text(
                                "${item.folderName}${item.subFolderName?.let { " -> $it" } ?: ""}",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.primary
                            )
                        }
                        if (!item.description.isNullOrBlank()) {
                            Text(item.description, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 2)
                        }
                        item.files.orEmpty().forEach { url ->
                            TextButton(onClick = { onOpenFile(url, item.title, item.files.orEmpty()) }) {
                                Text(displayFileName(url))
                            }
                        }
                    }
                }
            }
        }
    }
}
